import { useEffect, useMemo, useState } from 'react'
import { formatMoney, formatQty } from '../lib/format.js'

// Order confirmation modal — opens when the manager taps
// "Send Order to [supplier]" on the Reorder screen.
//
// Each line item exposes two editable numbers:
//   on_hand_now    — quick stocktake; saved back to items.current_on_hand
//   order_qty      — what to order this round
//
// When on_hand changes, suggested order_qty auto-updates to
// (par_qty - on_hand). Manager can override either.
//
// On submit, calls onConfirm(lineItems, notes). Parent's sendOrder()
// handles the DB writes.
export default function OrderConfirmModal({
  open,
  onClose,
  onConfirm,
  group,        // { supplier, supplierName, items }
}) {
  const [lines, setLines] = useState([])  // editable per-row state
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Initialize editable state when the modal opens or the supplier
  // group changes. Default order_qty = par - on_hand.
  useEffect(() => {
    if (!open || !group) return
    const initial = group.items.map((i) => {
      const onHand = Number(i.current_on_hand)
      const par    = Number(i.par_qty)
      const need   = Math.max(0, par - onHand)
      return {
        item_id:        i.id,
        item_name:      i.name,
        unit:           i.unit,
        cost_per_unit:  Number(i.cost_per_unit),
        par_qty:        par,
        on_hand:        onHand,    // editable
        order_qty:      need,      // editable
        original_on_hand: onHand,  // tracked so we know if to send a stocktake update
      }
    })
    setLines(initial)
    setNotes('')
    setError(null)
  }, [open, group])

  // Update a single line's field. When on_hand changes, also
  // re-suggest order_qty (manager can still override after).
  function updateLine(idx, field, value) {
    setLines((prev) => {
      const next = [...prev]
      const line = { ...next[idx], [field]: value }
      if (field === 'on_hand') {
        const onHand = Number(value)
        const par    = Number(line.par_qty)
        const need   = Math.max(0, par - (isFinite(onHand) ? onHand : 0))
        line.order_qty = need
      }
      next[idx] = line
      return next
    })
  }

  function removeLine(idx) {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const grandTotal = useMemo(() => {
    return lines.reduce((sum, l) => {
      const q = Number(l.order_qty)
      return sum + (isFinite(q) && q > 0 ? q * l.cost_per_unit : 0)
    }, 0)
  }, [lines])

  const orderableLines = useMemo(
    () => lines.filter((l) => Number(l.order_qty) > 0),
    [lines]
  )

  async function handleSubmit() {
    if (submitting) return
    if (orderableLines.length === 0) {
      setError('Nothing to order — all line items have a quantity of 0.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const lineItems = orderableLines.map((l) => ({
        item_id: l.item_id,
        item_name: l.item_name,
        quantity: Number(l.order_qty),
        unit: l.unit,
        cost_per_unit: l.cost_per_unit,
        total_cost: Number((Number(l.order_qty) * l.cost_per_unit).toFixed(2)),
        // Only send a stocktake update if the manager actually
        // changed the on-hand number.
        new_on_hand:
          Number(l.on_hand) !== Number(l.original_on_hand)
            ? Number(l.on_hand)
            : null,
      }))
      // onConfirm is responsible for closing this modal AND
      // (optionally) opening the email modal — see ReorderSuggestionsScreen.
      await onConfirm({ lineItems, notes })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OrderConfirmModal]', e)
      setError('Could not send order. Check your connection and try again.')
    } finally {
      // Always reset — modal stays mounted between opens.
      setSubmitting(false)
    }
  }

  if (!open || !group) return null

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Send order to ${group.supplierName}`}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">
              Send order — {group.supplierName}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900/80">
            Update each item's <strong>on-hand</strong> count if it's
            wrong (quick stocktake), then confirm the suggested order
            quantity. We'll log the order and update inventory.
          </div>

          {/* Line items */}
          <ul className="space-y-3">
            {lines.map((l, idx) => {
              const lineTotal = Number(l.order_qty) * l.cost_per_unit
              return (
                <li
                  key={l.item_id}
                  className="rounded-xl border border-black/10 p-3"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-medium">{l.item_name}</div>
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      aria-label={`Remove ${l.item_name} from order`}
                      title="Remove from this order"
                      className="rounded p-1 text-xs text-black/40 hover:bg-red-50 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="mt-1 text-xs text-black/60">
                    Par {formatQty(l.par_qty, l.unit)} · {formatMoney(l.cost_per_unit)}/{l.unit}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-0.5 block text-xs font-medium text-black/70">
                        On hand now
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={l.on_hand}
                        onChange={(e) => updateLine(idx, 'on_hand', e.target.value)}
                        className="w-full rounded-lg border border-black/15 px-3 py-2 text-lg font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs font-medium text-black/70">
                        Order qty
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={l.order_qty}
                        onChange={(e) => updateLine(idx, 'order_qty', e.target.value)}
                        className="w-full rounded-lg border border-black/15 px-3 py-2 text-lg font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                      />
                    </div>
                  </div>

                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-black/60">Line total</span>
                    <span className="font-medium tabular-nums">
                      {formatMoney(isFinite(lineTotal) ? Math.max(0, lineTotal) : 0)}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="po-notes">
              Notes (optional)
            </label>
            <textarea
              id="po-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. need by Friday before lunch"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Error moved to sticky header. */}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-black/60">
              {orderableLines.length} of {lines.length} line items
            </span>
            <span className="text-lg font-medium tabular-nums">
              {formatMoney(grandTotal)}
            </span>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || orderableLines.length === 0}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-2xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Sending…'
              : `Confirm & Send · ${formatMoney(grandTotal)}`}
          </button>
        </div>
      </div>
    </div>
  )
}
